import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CampaignIcon from '@mui/icons-material/Campaign';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CampaignSchema, type CampaignDto, type EmployeeDto } from '@taqon/contracts';
import { useNavigate, useSearchParams } from 'react-router';
import { getPage, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog } from '../../shared/ui/forms';
import { ExportButton } from '../../shared/export/ExportButton';
import { exportHref } from '../../shared/export/export-href';
import { SearchFilterBar } from '../../shared/ui/filters';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { ActiveEmployeePicker, EmployeeName, ListSelect, Pager } from '../crm/common';
import { RecordLink } from '../finance/detail';
import { isAmount } from '../finance/money';
import { aed, CAMPAIGNS, useCampaignLists, valueLabel } from './common';

const PAGE_SIZE = 50;

/**
 * Campaigns (FRD-MKT-017, MKT-045): operational campaign reporting by status, channel, target
 * audience, owner and period (a campaign is shown for a period its schedule overlaps), with the
 * planned budget — filtered and paged on the server and kept in the URL. The Campaign ID is
 * generated by the system.
 */
export function CampaignsPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'marketing.campaign:create');
  const canSeeEmployees = hasCapability(session.data, 'hr.employee:view');
  const lists = useCampaignLists();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [owner, setOwner] = useState<EmployeeDto | null>(null);
  const [search, setSearch] = useState(params.get('search') ?? '');

  const filters = {
    status: params.get('status') ?? '',
    channel: params.get('channel') ?? '',
    audience: params.get('audience') ?? '',
    owner: params.get('owner') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    search: params.get('search') ?? '',
  };
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const set = (key: keyof typeof filters | 'page', value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };

  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(PAGE_SIZE),
    sort: '-start_date',
  });
  const apiFilters: readonly [string, string][] = [
    ['status', filters.status],
    ['channel', filters.channel],
    ['target_audience', filters.audience],
    ['owner_employee_id', filters.owner],
    ['period_from', filters.from],
    ['period_to', filters.to],
    ['search', filters.search],
  ];
  for (const [key, value] of apiFilters) if (value !== '') query.set(`filter[${key}]`, value);
  const campaigns = useQuery({
    queryKey: ['marketing', 'campaigns', 'list', query.toString()],
    queryFn: () => getPage(`${CAMPAIGNS}?${query.toString()}`, CampaignSchema),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Campaigns"
        description="Campaign plans, schedules, planned budgets and recorded results."
        actions={
          <>
            <ExportButton recordType="marketing.campaign" href={exportHref(CAMPAIGNS, query)} />
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<CampaignIcon />}
                onClick={(event) => {
                  event.currentTarget.focus();
                  setCreating(true);
                }}
              >
                New campaign
              </Button>
            )}
          </>
        }
      />
      <SearchFilterBar
        aria-label="Campaign filters"
        label="Search by ID or name"
        value={search}
        onChange={setSearch}
        onBlur={() => {
          if (search.trim() !== filters.search) set('search', search.trim());
        }}
        onSubmit={() => {
          set('search', search.trim());
        }}
      >
        {(
          [
            ['status', 'Status', lists.data?.status],
            ['channel', 'Channel', lists.data?.channel],
            ['audience', 'Target audience', lists.data?.targetAudience],
          ] as const
        ).map(([key, label, values]) => (
          <TextField
            key={key}
            select
            size="small"
            label={label}
            value={filters[key]}
            onChange={(e) => {
              set(key, e.target.value);
            }}
          >
            <MenuItem value="">All</MenuItem>
            {(values ?? []).map((value) => (
              <MenuItem key={value.valueCode} value={value.valueCode}>
                {value.label}
                {value.active ? '' : ' (inactive)'}
              </MenuItem>
            ))}
          </TextField>
        ))}
        <TextField
          size="small"
          type="date"
          label="Period from"
          value={filters.from}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            set('from', e.target.value);
          }}
        />
        <TextField
          size="small"
          type="date"
          label="Period to"
          value={filters.to}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            set('to', e.target.value);
          }}
        />
        {canSeeEmployees &&
          (filters.owner !== '' && owner === null ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2">
                Owner: <EmployeeName id={filters.owner} />
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  set('owner', '');
                }}
              >
                Clear
              </Button>
            </Stack>
          ) : (
            <ActiveEmployeePicker
              label="Owner"
              value={owner}
              onChange={(value) => {
                setOwner(value);
                set('owner', value?.id ?? '');
              }}
            />
          ))}
      </SearchFilterBar>
      {campaigns.isError && <ErrorState message={campaigns.error.message} />}
      <Section>
        {campaigns.isPending && <LoadingState label="Loading campaigns" />}
        {campaigns.data?.items.length === 0 && <EmptyState title="No campaigns found" />}
        {campaigns.data !== undefined && campaigns.data.items.length > 0 && (
          <>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label="Campaigns">
                <TableHead>
                  <TableRow>
                    <TableCell>Campaign ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell>Schedule</TableCell>
                    <TableCell align="right">Planned budget</TableCell>
                    <TableCell>Owner</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.data.items.map((campaign) => (
                    <TableRow key={campaign.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <RecordLink to={`/marketing/campaigns/${campaign.id}`}>
                          {campaign.code}
                        </RecordLink>
                      </TableCell>
                      <TableCell>{campaign.name}</TableCell>
                      <TableCell>{valueLabel(lists.data?.status, campaign.statusCode)}</TableCell>
                      <TableCell>{valueLabel(lists.data?.channel, campaign.channelCode)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{schedule(campaign)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {aed(campaign.budget)}
                      </TableCell>
                      <TableCell>
                        <EmployeeName id={campaign.ownerEmployeeId} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={campaigns.data.total}
              noun="campaign(s)"
              onPage={(next) => {
                set('page', String(next));
              }}
            />
          </>
        )}
      </Section>
      {creating && (
        <CampaignDialog
          onClose={(saved) => {
            setCreating(false);
            if (saved !== undefined) void navigate(`/marketing/campaigns/${saved.id}?created=1`);
          }}
        />
      )}
    </Stack>
  );
}

/** A campaign's schedule; no end date is open-ended. */
export function schedule(campaign: CampaignDto): string {
  return `${campaign.startDate} – ${campaign.endDate ?? 'open'}`;
}

interface FormValues {
  name: string;
  objective: string;
  startDate: string;
  endDate: string;
  budget: string;
  targetAudienceCode: string;
  channelCode: string;
  statusCode: string;
  attributionModel: string;
  results: string;
}

const OPTIONAL: ReadonlySet<keyof FormValues> = new Set([
  'objective',
  'endDate',
  'budget',
  'targetAudienceCode',
  'channelCode',
  'attributionModel',
  'results',
]);

function valuesOf(campaign: CampaignDto | undefined): FormValues {
  return {
    name: campaign?.name ?? '',
    objective: campaign?.objective ?? '',
    startDate: campaign?.startDate ?? '',
    endDate: campaign?.endDate ?? '',
    budget: campaign?.budget ?? '',
    targetAudienceCode: campaign?.targetAudienceCode ?? '',
    channelCode: campaign?.channelCode ?? '',
    statusCode: campaign?.statusCode ?? '',
    attributionModel: campaign?.attributionModel ?? '',
    results: campaign?.results ?? '',
  };
}

/**
 * Create (no `campaign`) or edit one campaign: its plan — objective, audience, channel, budget,
 * owner and schedule (FRD-MKT-020). An edit sends only what changed, with If-Match. The status and
 * the results have their own actions on the campaign page.
 */
export function CampaignDialog({
  campaign,
  onClose,
}: {
  campaign?: CampaignDto;
  onClose: (saved?: CampaignDto) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const lists = useCampaignLists();
  const canSeeEmployees = hasCapability(session.data, 'hr.employee:view');
  const creating = campaign === undefined;
  const original = valuesOf(campaign);
  const [values, setValues] = useState<FormValues>(original);
  const [owner, setOwner] = useState<EmployeeDto | null>(null);
  const set = (field: keyof FormValues) => (value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  const body: Record<string, string | null> = {};
  for (const field of Object.keys(values) as (keyof FormValues)[]) {
    if (!creating && (field === 'statusCode' || field === 'results')) continue;
    const value = values[field].trim();
    if (creating) {
      if (value !== '') body[field] = value;
    } else if (value !== original[field]) {
      body[field] = OPTIONAL.has(field) && value === '' ? null : value;
    }
  }
  if (owner !== null && owner.id !== campaign?.ownerEmployeeId) body.ownerEmployeeId = owner.id;
  const budgetValid = values.budget.trim() === '' || isAmount(values.budget);
  const periodValid = values.endDate === '' || values.endDate >= values.startDate;
  const complete =
    values.name.trim() !== '' &&
    values.startDate !== '' &&
    values.statusCode !== '' &&
    budgetValid &&
    periodValid &&
    (!creating || owner !== null);

  const save = useMutation({
    mutationFn: () =>
      creating
        ? postJson(CAMPAIGNS, body, CampaignSchema, session.data?.csrfToken ?? '')
        : patchJson(
            `${CAMPAIGNS}/${campaign.id}`,
            body,
            CampaignSchema,
            session.data?.csrfToken ?? '',
            campaign.version,
          ),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['marketing', 'campaigns'] });
      await queryClient.invalidateQueries({ queryKey: ['marketing', 'campaign'] });
      onClose(saved);
    },
  });

  const text = (field: keyof FormValues, label: string, type = 'text') => (
    <TextField
      label={label}
      type={type}
      value={values[field]}
      slotProps={type === 'date' ? { inputLabel: { shrink: true } } : {}}
      onChange={(e) => {
        set(field)(e.target.value);
      }}
    />
  );

  return (
    <FormDialog
      open
      title={creating ? 'New campaign' : `Edit campaign ${campaign.code}`}
      submitLabel={creating ? 'Create campaign' : 'Save changes'}
      canSubmit={complete && Object.keys(body).length > 0}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={() => {
        onClose();
      }}
      onSubmit={() => {
        save.mutate();
      }}
    >
      {creating ? (
        <Alert severity="info" variant="outlined">
          The Campaign ID is generated by the system when the campaign is saved.
        </Alert>
      ) : (
        <TextField label="Campaign ID" value={campaign.code} disabled />
      )}
      <TextField
        label="Name"
        required
        autoFocus={creating}
        value={values.name}
        onChange={(e) => {
          set('name')(e.target.value);
        }}
      />
      <TextField
        label="Objective"
        multiline
        minRows={2}
        value={values.objective}
        onChange={(e) => {
          set('objective')(e.target.value);
        }}
      />
      <TextField
        label="Start date"
        type="date"
        required
        value={values.startDate}
        slotProps={{ inputLabel: { shrink: true } }}
        onChange={(e) => {
          set('startDate')(e.target.value);
        }}
      />
      <TextField
        label="End date"
        type="date"
        value={values.endDate}
        error={!periodValid}
        helperText={periodValid ? 'Optional' : 'The end date is before the start date.'}
        slotProps={{ inputLabel: { shrink: true } }}
        onChange={(e) => {
          set('endDate')(e.target.value);
        }}
      />
      <TextField
        label="Planned budget (AED)"
        value={values.budget}
        error={!budgetValid}
        helperText={
          budgetValid
            ? 'Planning information only; not an accounting budget.'
            : 'An amount with at most 2 decimals.'
        }
        onChange={(e) => {
          set('budget')(e.target.value);
        }}
      />
      {creating && (
        <ListSelect
          label="Status"
          required
          values={lists.data?.status}
          value={values.statusCode}
          onChange={set('statusCode')}
        />
      )}
      <ListSelect
        label="Channel"
        values={lists.data?.channel}
        value={values.channelCode}
        current={campaign?.channelCode}
        onChange={set('channelCode')}
      />
      <ListSelect
        label="Target audience"
        values={lists.data?.targetAudience}
        value={values.targetAudienceCode}
        current={campaign?.targetAudienceCode}
        onChange={set('targetAudienceCode')}
      />
      {canSeeEmployees ? (
        <ActiveEmployeePicker
          label={creating ? 'Owner' : 'Change owner'}
          required={creating}
          value={owner}
          onChange={setOwner}
          helperText={creating ? 'The employee responsible for the campaign.' : 'Optional'}
        />
      ) : (
        <Alert severity="warning" variant="outlined">
          Choosing the owner needs permission to view employees.
        </Alert>
      )}
      {text('attributionModel', 'Attribution model')}
    </FormDialog>
  );
}
